import { useMemo } from "react";

/**
 * The argument made visceral. Two stacked freshness curves over 60 seconds:
 *  - Block-time oracle: sawtooth — peaks at the inter-update gap, drops on
 *    each refresh. Average freshness == half the refresh interval.
 *  - Noeracle: nearly flat near 0.3s — signing cadence sets the ceiling,
 *    not ledger close time.
 *
 * No naming-and-shaming of any specific competitor (per Yahya's call). Just
 * the math, drawn as a chart.
 */
export function LatencyThesis() {
  const { blockPath, blockFillPath, pullPath, pullFillPath, viewBox } =
    useMemo(buildPaths, []);

  return (
    <section
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32"
      aria-labelledby="thesis-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> THE LATENCY THESIS
      </p>
      <h2
        id="thesis-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        <em className="italic">Freshness</em> bounded by signing cadence,
        <br />
        not ledger close time.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Push oracles refresh on a schedule — the price your contract reads
        ages until the next update lands. Pull oracles refresh inside your
        transaction. The age of the price you trade against is bounded by
        signing cadence, not block intervals.
      </p>

      <div className="mt-10 rounded-xl border border-border bg-surface/40 p-5 sm:p-8">
        {/* Chart */}
        <div className="relative">
          <svg
            viewBox={viewBox}
            preserveAspectRatio="none"
            className="w-full h-[260px] sm:h-[320px]"
            role="img"
            aria-label="Price age over time: block-time oracle saw-tooths to 5s, Noeracle stays under 0.5s"
          >
            {/* Grid — match the 0/15/30/45/60s tick lines */}
            {[0, 15, 30, 45, 60].map((s) => (
              <line
                key={s}
                x1={0}
                x2={600}
                y1={300 - (s / 60) * 280}
                y2={300 - (s / 60) * 280}
                stroke="var(--color-hairline)"
                strokeWidth={1}
              />
            ))}
            {/* Y-axis labels — 0 to 60s, ticks every 15s */}
            {[0, 15, 30, 45, 60].map((s) => (
              <text
                key={s}
                x={-6}
                y={300 - (s / 60) * 280 + 3}
                textAnchor="end"
                fontFamily="var(--font-mono)"
                fontSize={9}
                fill="var(--color-m40)"
              >
                {s}s
              </text>
            ))}

            {/* Block oracle area + line (amber, warning) */}
            <path d={blockFillPath} fill="rgba(255,181,71,0.06)" />
            <path
              d={blockPath}
              fill="none"
              stroke="var(--color-amber)"
              strokeWidth={1.5}
              strokeLinejoin="round"
            />

            {/* Noeracle area + line (emerald) */}
            <path d={pullFillPath} fill="rgba(127,255,140,0.10)" />
            <path
              d={pullPath}
              fill="none"
              stroke="var(--color-emerald)"
              strokeWidth={1.75}
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Legend / stats below the chart */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Stat
            color="bg-amber"
            label="Push oracle (typical)"
            value="avg 30s · worst 1 min"
            note="Refreshes on a fixed cadence — freshness sawtooths. Best-case push oracles refresh every ~5s."
          />
          <Stat
            color="bg-emerald"
            label="Noeracle (pull)"
            value="avg 0.25s · worst 0.5s"
            note="Refreshes inside your transaction — cadence bounded by 500ms signing."
          />
        </div>
      </div>
    </section>
  );
}

function Stat({
  color,
  label,
  value,
  note,
}: {
  color: string;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-bg-deep/50 px-4 py-3">
      <span className={`mt-1.5 inline-block size-2 rounded-[1px] ${color}`} />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-m55">
          {label}
        </div>
        <div className="mt-1 font-mono text-[14px] tabular text-text">
          {value}
        </div>
        <div className="mt-1 text-[12px] leading-snug text-m55">{note}</div>
      </div>
    </div>
  );
}

/* ---------- path construction ---------- */

function buildPaths() {
  // SVG viewBox: 600 wide × 320 tall.
  // X axis: ~3 minutes (180s) of timeline — enough to show 3 cycles of a
  // typical 60s push oracle. Y axis: 0 to 60s of price age.
  // Noeracle's ~0.3s sits near zero on this scale — that *is* the argument.
  const W = 600;
  const H = 320;
  const top = 20;
  const bottom = 300;
  const plotH = bottom - top;
  const yMax = 60; // 0–60 seconds of price age
  const window = 180; // 3 minutes shown
  const x = (sec: number) => (sec / window) * W;
  const y = (s: number) => bottom - (Math.min(s, yMax) / yMax) * plotH;

  const step = 0.5;
  const blockPeriod = 60; // typical push oracle update interval

  // Push sawtooth: linear ramp from 0 to blockPeriod, snap back at boundary.
  let blockD = `M ${x(0)} ${y(0)}`;
  for (let t = step; t <= window; t += step) {
    const phase = t % blockPeriod;
    if (phase < step) {
      blockD += ` L ${x(t)} ${y(blockPeriod)}`; // up to peak
      blockD += ` L ${x(t)} ${y(0)}`; // sharp drop
    } else {
      blockD += ` L ${x(t)} ${y(phase)}`;
    }
  }
  const blockFillD = blockD + ` L ${x(window)} ${y(0)} L ${x(0)} ${y(0)} Z`;

  // Noeracle: hovers near 0.3s with mild sinusoidal variation. At Y scale
  // of 0-60s this reads as a flat line near the baseline.
  let pullD = `M ${x(0)} ${y(0.3)}`;
  for (let t = step; t <= window; t += step) {
    const s = 0.28 + 0.08 * Math.sin(t * 0.6) + 0.04 * Math.sin(t * 1.1);
    pullD += ` L ${x(t)} ${y(Math.max(0, s))}`;
  }
  const pullFillD = pullD + ` L ${x(window)} ${y(0)} L ${x(0)} ${y(0)} Z`;

  return {
    blockPath: blockD,
    blockFillPath: blockFillD,
    pullPath: pullD,
    pullFillPath: pullFillD,
    viewBox: `0 0 ${W} ${H}`,
  };
}
